import { CSSProperties, ForwardedRef, forwardRef, useCallback, useEffect, useMemo, useState } from "react";

import { LoadingGroup } from "capitalrx-components-2.0/lib/general";
import { Body } from "capitalrx-components-2.0/lib/typography";
import classNames from "classnames";
import IMask, { FactoryArg, InputMask } from "imask";
import { DateTime, FixedOffsetZone } from "luxon";

import { useId } from "../../hooks/useId";
import { getBasePropsFromHtmlProps } from "../../utilities/getBasePropsFromHtmlProps";
import { getHtmlPropsFromBaseProps } from "../../utilities/getHtmlPropsFromBaseProps";
import { useInputDisabled, useInputReadonly } from "../InputGroup";
import { useSubmitting } from "../SubmitGroup";
import { Overlay } from "../Textbox/Overlay";
import { Placeholder } from "../Textbox/Placeholder";
import { StyledNativeInput } from "../Textbox/StyledNativeInput";
import { useValidity } from "../ValidityGroup";
import { InputContainer } from "../shared";
import { normalizeAutocompleteValue } from "../utils/autocompleteHelpers";
import { DateboxProps } from "./Datebox";

import styles from "../Textbox/styles.module.scss";

export const DateFormat = "LL/dd/yyyy";

/**
 * The `Input` component properties.
 */
export interface InputProps extends DateboxProps {
  /**
   * Indicates whether the input is required.
   */
  readonly required?: boolean;
}

/**
 * An input field component which allows entry of a Date value.
 */
export const Input = forwardRef(function Input(props: InputProps, outerRef: ForwardedRef<HTMLInputElement>) {
  const {
    label,
    zone = "utc",
    required = false,
    tooltip,
    instructions,
    hideInstructions,
    hideLabel,
    prefix,
    suffix,
    defaultValue = null,
    autoFocus,
    tabIndex,
    onChange,
    onFocusEnter,
    onFocusExit,
    autoComplete,
  } = props;

  const { value = defaultValue } = props;

  const baseHtmlProps = getHtmlPropsFromBaseProps(props, {
    className: [styles.Textbox, "datebox"],
    testIdPrefix: "datebox",
  });

  const [mask, setMask] = useState<InputMask<FactoryArg> | null>(null);
  const id = useId();
  const disabled = useInputDisabled(props);
  const readOnly = useInputReadonly(props);
  const submitting = useSubmitting();
  const [invalid] = useValidity();

  const [prefixSize, setPrefixSize] = useState<number | null>(null);
  const [suffixSize, setSuffixSize] = useState<number | null>(null);
  const [currentElement, setCurrentElement] = useState<HTMLInputElement | null>(null);

  const refChangeHandler = useCallback((ref: HTMLInputElement | null) => {
    setCurrentElement(ref);

    setMask((current) => {
      if (!ref && current) {
        current.destroy();
        return null;
      }

      if (ref && !current) {
        // eslint-disable-next-line new-cap
        return IMask(ref, {
          mask: "m`/d`/y",
          lazy: false,
          overwrite: true,
          autofix: true,
          blocks: {
            d: { mask: IMask.MaskedRange, from: 1, to: 31, maxLength: 2, placeholderChar: "_" },
            m: { mask: IMask.MaskedRange, from: 1, to: 12, maxLength: 2, placeholderChar: "_" },
            y: { mask: IMask.MaskedRange, from: 0, to: 9999, placeholderChar: "_" },
          },
        });
      }

      return current;
    });
  }, []);

  useEffect(() => {
    if (outerRef) {
      if (typeof outerRef === "function") {
        outerRef(currentElement);
      } else {
        outerRef.current = currentElement;
      }
    }

    return () => {
      if (outerRef) {
        if (typeof outerRef === "function") {
          outerRef(null);
        } else {
          outerRef.current = null;
        }
      }
    };
  }, [outerRef, currentElement]);

  const changeHandler = useCallback(() => {
    if (disabled) return;
    if (readOnly || submitting) return;
    if (!mask) return;

    const date = DateTime.fromFormat(mask.value, DateFormat, {
      setZone: true,
      zone: zone === "utc" ? FixedOffsetZone.utcInstance : "local",
    });

    if (!date.isValid)
      // An invalid value that consists entirely as the mask value should just return out as a blank value.
      onChange?.(mask.value === "__/__/____" ? null : { type: "invalid", value: mask.value, reason: date.invalidReason }, value);
    else onChange?.(date.toJSDate(), value);
  }, [mask, zone, value, disabled, readOnly, submitting, onChange]);

  useEffect(() => {
    mask?.on("accept", changeHandler);

    return () => {
      mask?.off("accept", changeHandler);
    };
  }, [mask, changeHandler]);

  const focusHandler = useCallback(() => {
    onFocusEnter?.();
  }, [onFocusEnter]);

  const blurHandler = useCallback(() => {
    onFocusExit?.();
  }, [onFocusExit]);

  const overlayLayoutHandler = useCallback((prefixSize: number | null, suffixSize: number | null) => {
    setPrefixSize(prefixSize);
    setSuffixSize(suffixSize);
  }, []);

  const autocompleteValue = useMemo(() => normalizeAutocompleteValue(autoComplete), [autoComplete]);

  const style = useMemo(() => {
    if (prefixSize === null && suffixSize === null) return {};
    const result: CSSProperties = {};
    if (prefixSize !== null) result.paddingLeft = `${prefixSize}px`;
    if (suffixSize !== null) result.paddingRight = `${suffixSize}px`;
    return result;
  }, [prefixSize, suffixSize]);

  useEffect(() => {
    if (!mask) return;

    let newValue = "";

    if (value !== null) {
      if (value instanceof Date) {
        newValue = DateTime.fromJSDate(value, { zone: zone === "utc" ? FixedOffsetZone.utcInstance : "local" }).toFormat(DateFormat);
      } else if (value.type === "invalid") {
        newValue = value.value;
      }
    }

    mask.value = newValue;
  }, [mask, value, zone]);

  return (
    <LoadingGroup.Placeholder fallback={<Placeholder className={props.className} style={props.style} hasInstructions={!!instructions} />}>
      <InputContainer
        {...getBasePropsFromHtmlProps(baseHtmlProps)}
        id={id}
        label={label}
        instructions={instructions}
        disabled={disabled}
        readOnly={readOnly}
        required={required}
        tooltip={tooltip}
        hideInstructions={hideInstructions}
        hideLabel={hideLabel}
      >
        <Body className="input-container">
          <StyledNativeInput
            ref={refChangeHandler}
            id={id}
            aria-labelledby={`${id}-label`}
            aria-invalid={invalid}
            aria-required={required}
            className={classNames("input", { invalid })}
            type="text"
            disabled={disabled}
            autoFocus={autoFocus}
            readOnly={readOnly || submitting}
            tabIndex={disabled || readOnly || submitting ? -1 : (tabIndex ?? 0)}
            style={style}
            autoComplete={autocompleteValue}
            onFocus={focusHandler}
            onBlur={blurHandler}
          />
          <Overlay prefix={prefix} suffix={suffix} onLayout={overlayLayoutHandler} />
        </Body>
      </InputContainer>
    </LoadingGroup.Placeholder>
  );
});
